import { AsyncMultiselect, SearchableMultiselect, SearchableMultiselectOption } from 'components';
import { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Customer, Location, Role, TruckingCompany, UserProfile } from 'types';

import { fetchPaginated } from 'services';
import { formatCustomer, formatTruckingCompany } from 'utils/format';
import './new-user.scss';

type UserCustomersProps = {
  user: Partial<UserProfile>;
  role: Role;
  customers: Map<number, Customer>;
  allCustomers: Map<number, Customer>;
  setUser: (user: Partial<UserProfile>) => void;
  disabled?: boolean;
};

export const UserCustomers: FC<UserCustomersProps> = ({
  user,
  role,
  customers,
  allCustomers,
  setUser,
  disabled = false
}) => {
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if ([Role.ADMIN_CUSTOMER, Role.CUSTOMER, Role.EXTERNAL].includes(role) && customers.size < 1) {
      setError('Please select at least one customer(s).');
      return;
    }

    setError('');
  }, [role, customers]);

  const handleAddCustomer = useCallback(
    (id: number) => {
      if (!customers.has(id)) {
        const customer = allCustomers.get(id);
        if (customer !== undefined) {
          setUser({ ...user, customers: [...customers.values(), customer] });
        }
      }
    },
    [allCustomers, customers, setUser, user]
  );

  const handleRemoveCustomer = useCallback(
    (id: number) => {
      if (customers.has(id)) {
        customers.delete(id);
        setUser({ ...user, customers: [...customers.values()] });
      }
    },
    [customers, setUser, user]
  );

  const handleSelect = useCallback(
    (option: SearchableMultiselectOption) => {
      if (option.selected) {
        handleAddCustomer(option.id);
      } else {
        handleRemoveCustomer(option.id);
      }
    },
    [handleAddCustomer, handleRemoveCustomer]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4 className="text-uppercase">account(s)</h4>
      </div>
      <div className="card-body user-association-input">
        <SearchableMultiselect
          onSelect={handleSelect}
          options={[...allCustomers.values()].map(({ id, name, location }) => ({
            id,
            selected: customers.has(id),
            value: formatCustomer({ name, location })
          }))}
          error={error}
          disabled={disabled}
          data-cy="user-accounts-input"
        />
        <div className="d-flex flex-column align-items-start g-2 mt-2">
          {customers.size > 0 ? (
            [...customers.values()].map(({ id, name }) => {
              return (
                <div className="tag g-2" key={id} data-cy={`edit-user-account-${id}-tag`}>
                  <p className="text-primary">{name}</p>
                  {!disabled && (
                    <button type="button" className="tag-close" onClick={() => handleRemoveCustomer(id)}>
                      <span className="material-icons">close</span>
                    </button>
                  )}
                </div>
              );
            })
          ) : (
            <div className="tag" key="all">
              <p className="text-primary text-uppercase">All</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type UserLocationsProps = {
  user: Partial<UserProfile>;
  role: Role;
  locations: Map<number, Location>;
  allLocations: Map<number, Location>;
  setUser: (user: Partial<UserProfile>) => void;
  disabled?: boolean;
};

export const UserLocations: FC<UserLocationsProps> = ({
  user,
  role,
  locations,
  allLocations,
  setUser,
  disabled = false
}) => {
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if ((role === Role.BOOKING || role === Role.GATE || role === Role.GATE_BOOKING) && locations.size !== 1) {
      setError('Please select one location.');
      return;
    }

    setError('');
  }, [role, locations.size]);

  const handleAddLocation = useCallback(
    (id: number) => {
      if (!locations.has(id)) {
        const location = allLocations.get(id);
        if (location !== undefined) {
          setUser({ ...user, locations: [...locations.values(), location] });
        }
      }
    },
    [allLocations, locations, setUser, user]
  );

  const handleRemoveLocation = useCallback(
    (id: number) => {
      if (locations.has(id)) {
        locations.delete(id);
        if (locations.size === 0) {
          setUser({ ...user, defaultLocation: undefined, locations: [...locations.values()] });
        } else {
          setUser({
            ...user,
            defaultLocation: user.defaultLocation?.id ? locations.get(user.defaultLocation.id) : undefined,
            locations: [...locations.values()]
          });
        }
      }
    },
    [locations, setUser, user]
  );

  const handleSelect = useCallback(
    (option: SearchableMultiselectOption) => {
      if (option.selected) {
        handleAddLocation(option.id);
      } else {
        handleRemoveLocation(option.id);
      }
    },
    [handleAddLocation, handleRemoveLocation]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4 className="text-uppercase">location(s)</h4>
      </div>
      <div className="card-body user-association-input">
        <div>
          <SearchableMultiselect
            onSelect={handleSelect}
            options={[...allLocations.values()].map(({ id, name, state }) => ({
              id,
              selected: locations.has(id),
              value: `${name} (${state})`
            }))}
            error={error}
            disabled={disabled}
            data-cy="user-locations-input"
          />
        </div>
        <div className="d-flex flex-column align-items-start g-2 mt-2">
          {locations.size > 0 ? (
            [...locations.values()].map(({ id, name, state }) => (
              <div className="tag g-2" key={id} data-cy={`edit-user-location-${id}-tag`}>
                <p className="text-primary">
                  {name}&nbsp;({state?.slice(0, 2).toUpperCase() ?? 'N/A'})
                </p>
                {!disabled && (
                  <button type="button" className="tag-close" onClick={() => handleRemoveLocation(id)}>
                    <span className="material-icons">close</span>
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="tag" key="all">
              <p className="text-primary text-uppercase">All</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

type UserTruckingCompaniesProps = {
  user: Partial<UserProfile>;
  role: Role;
  setUser: Dispatch<SetStateAction<Partial<UserProfile>>>;
  disabled?: boolean;
};

export const UserTruckingCompanies: FC<UserTruckingCompaniesProps> = ({ user, role, setUser, disabled = false }) => {
  const [error, setError] = useState<string>('');

  const truckingCompanies: TruckingCompany[] = useMemo(() => {
    return user?.truckingCompanies ?? [];
  }, [user.truckingCompanies]);

  const selection: Map<number, TruckingCompany | null> = useMemo(() => {
    return truckingCompanies.reduce((acc, val) => acc.set(val.id, val), new Map());
  }, [truckingCompanies]);

  useEffect(() => {
    if ((role === Role.CARRIER || role === Role.EXTERNAL) && truckingCompanies.length < 1) {
      setError('Please select at least one trucking company(s).');
      return;
    }
    setError('');
  }, [role, truckingCompanies]);

  const handleSelect = useCallback(
    (action: SetStateAction<Map<number, TruckingCompany | null>>) => {
      const newSelection = typeof action === 'function' ? action(selection) : action;
      setUser(prevState => ({
        ...prevState,
        truckingCompanies: [...newSelection.values()].filter(val => !!val) as TruckingCompany[]
      }));
    },
    [selection, setUser]
  );

  return (
    <div className="card-primary">
      <div className="card-header px-3">
        <h4 className="text-uppercase">trucking company(s)</h4>
      </div>
      <div className="card-body trucking-companies user-association-input">
        <AsyncMultiselect<TruckingCompany>
          id="trucking-company-async-multiselect"
          error={error}
          disabled={disabled}
          fetchData={fetchPaginated('trucking-companies/page')}
          formatOption={formatTruckingCompany}
          handleSelect={handleSelect}
          rowSelection={selection}
          sortField="name"
          data-cy="user-carriers-search"
        />
        <div className="d-flex flex-column align-items-start g-2 mt-2">
          {truckingCompanies.map(({ id, name, scacCode }) => {
            return (
              <div className="tag g-2" key={id} data-cy={`edit-user-carrier-${id}-tag`}>
                <p className="text-primary">
                  {name}&nbsp;({scacCode.toUpperCase()})
                </p>
                {!disabled && (
                  <button
                    type="button"
                    className="tag-close"
                    onClick={() => handleSelect(prev => new Map(prev).set(id, null))}
                  >
                    <span className="material-icons">close</span>
                  </button>
                )}
              </div>
            );
          })}
          {!truckingCompanies.length && (
            <div className="tag" key="all">
              <p className="text-primary text-uppercase">All</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
